import {
  Select as HeroUISelect,
  SelectItem as HeroUISelectItem,
  ListboxProps,
  PopoverProps,
} from "@heroui/react";
import clsx from "clsx";
import { WithId } from "core/types";
import { useMemo } from "react";
import { getMergedClassNames, mapVariant, type SelectProps } from "./utils";

function useSelectedKeysConvert<T>(selectedKeys: T): Set<string> {
  const stableKey = useMemo(() => {
    if (selectedKeys == null) return "";
    if (selectedKeys instanceof Set) {
      return Array.from(selectedKeys).sort().join(",");
    }
    if (Array.isArray(selectedKeys)) {
      return selectedKeys.map(String).sort().join(",");
    }
    return String(selectedKeys);
  }, [selectedKeys]);

  return useMemo(() => {
    if (selectedKeys == null) return new Set<string>();
    if (selectedKeys instanceof Set) return selectedKeys as Set<string>;
    if (Array.isArray(selectedKeys)) return new Set(selectedKeys.map(String));
    return new Set([String(selectedKeys)]);
  }, [stableKey]);
}

type Item = WithId & Record<string, unknown>;
export function Select<T extends WithId & Record<string, unknown> = Item>({
  "data-testid": dataTestId,
  disableAnimation = true,
  isDisabled,
  isInvalid,
  renderItem,
  selectedKeys,
  size,
  variant,
  ...props
}: SelectProps<T>) {
  const testId = dataTestId ?? props.name;
  const convertToSet = useSelectedKeysConvert(selectedKeys);
  const { labelPlacement, variant: variantMapped } = mapVariant(variant);
  const mergedClassNames = getMergedClassNames({
    isDisabled,
    isInvalid,
    size,
    variantProp: variant,
  });

  return (
    <HeroUISelect
      {...props}
      classNames={mergedClassNames}
      data-testid={testId}
      disableAnimation={disableAnimation}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      labelPlacement={labelPlacement}
      listboxProps={
        {
          className:
            "outline-hidden! ring-0! focus:outline-hidden! focus:ring-0!",
          "data-testid": `${testId}-listbox`,
        } as Partial<ListboxProps>
      }
      popoverProps={
        {
          className:
            "outline-hidden! ring-0! focus:outline-hidden! focus:ring-0!",
          "data-testid": `${testId}-popover`,
        } as Partial<PopoverProps>
      }
      selectedKeys={convertToSet}
      title={typeof props.title === "string" ? props.title : undefined}
      variant={variantMapped}
    >
      {(item: Item) => {
        const rendered = renderItem(item as T);
        const rawText =
          typeof rendered === "string" || typeof rendered === "number"
            ? String(rendered)
            : "";
        const textValue = rawText.trim().length > 0 ? rawText : String(item.id);
        return (
          <HeroUISelectItem
            key={item.id}
            classNames={{
              base: clsx(
                "text-gray-900!",
                "rounded-lg bg-white! p-2 ring-1 ring-transparent outline-hidden!",
                "data-[focus=true]:bg-primary-light! data-[focus=true]:ring-primary",
                "data-[hover=true]:bg-primary-light! data-[hover=true]:ring-primary",
                "data-[selected=true]:bg-primary-light! data-[selected=true]:font-semibold data-[selected=true]:ring-primary",
              ),
            }}
            data-testid={`${testId}-item-${item.id}`}
            textValue={textValue}
          >
            {rendered}
          </HeroUISelectItem>
        );
      }}
    </HeroUISelect>
  );
}
Select.displayName = "RecareUI.Select";
